import {h, Fragment} from "preact"
import { useFacets , useSort, useProductFilters, useAppStateSelector, pick} from "@nosto/preact"
import { useState, useEffect } from "preact/hooks"
import Facet from "./Facet"
import RangeFacet from "./RangeFacet"
import Select from "./elements/Select"
import FilterSelectGrebban from "./elements/FilterSelectGrebban"
import { sortOptions } from "../config"
import { translatedText } from "./utils/translatedText";


export const toggleButtonId = "toggle-mobile-sidebar"

export const GRID_CLASSES = {
    LARGE: "product-grid-large",
    SMALL: "product-grid-small",
  };
  
export const STORAGE_KEY = "preferredGridClass";

export const applyGridClass = (className) => {
    const container = document.getElementById("ProductGridContainer");
    if (!container) return;

    // Remove any existing grid layout class
    Object.values(GRID_CLASSES).forEach(cls => container.classList.remove(cls));

    // Add the new class
    container.classList.add(className);
};

// Function to update layout: apply class and save to localStorage
export const setGridLayout = (className) => {
    localStorage.setItem(STORAGE_KEY, className);
    applyGridClass(className);
};

function ToggleSidebarButton({ className }) {
    return (
        <label
            className={`ml-auto w-20 h-20 !flex justify-end ${className}`}
            for={toggleButtonId}
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17" stroke-width="0.5">
                <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor" stroke="currentColor">
            </path></svg>
        </label>
    )
}

export default function SideBar() {
    const { query, response } = useAppStateSelector(state =>
        pick(state, "query", "response")
      );
    const { loading, facets,selectedFiltersCount } = useFacets()
    const { activeSort, setSort } = useSort(sortOptions)
    const { filters, removeAll } = useProductFilters()
    const [activeFacet, setActiveFacet] = useState('');
    const [gridClass, setGridClass] = useState(GRID_CLASSES.LARGE);
    const [selectedItems, setSelectedItems] = useState([]);

    const handleSortChange = event => {
        setSort(event.target.value)
    }
    const options = sortOptions.map(o => ({ value: o.id, label: o.value.name }))
    
    // On mount, sync grid layout from localStorage and apply it
    useEffect(() => {
        const savedClass = localStorage.getItem(STORAGE_KEY) || GRID_CLASSES.LARGE;
        setGridLayout(savedClass);
        setGridClass(savedClass);
    }, []);

    const handleGridChange = (newClass) => {
        setGridLayout(newClass); // applies to DOM + localStorage
        setGridClass(newClass);  // updates local state for UI feedback
    };

    const docCount = response.products?.total ?? 0;
    const hasActiveFilters = (selectedFiltersCount ?? filters.length ?? 0) > 0;
    const addFilterLabel = hasActiveFilters
    ? `${translatedText("apply")} ${docCount > 0 ? `[${docCount}]` : ""}`
    : `${translatedText("seeAll")} ${docCount > 0 ? `[${docCount}]` : ""}`;
    const clearLabel = hasActiveFilters ? `${translatedText("clearAll")}` : ''
    
    return facets?.length > 0 ? (
        <>
            <input type="checkbox" id={toggleButtonId} class="ns-facet-sidebar-toggle ns-d-none" /> 
            {/* <label
                class="ns-sidebar-backdrop ns-fixed ns-w-100 ns-h-100 ns-background-black ns-d-none"
                for={toggleButtonId}
            /> */} 
            <div
                class="ns-sidebar-wrapper ns-d-none ns-d-l-block lg:!block ns-h-100 sticky top-combined-header-usp-height nosto-filter-bar"
            >
                <div class="ns-sidebar-content ns-background-white ns-h-100 ns-mr-0 ns-mr-md-auto fixed inset-0 w-full lg:relative">
                    <div class="flex p-8 align-top capitalize text-28-90--0.5-900 lg:text-32-100--0.5-900 lg:hidden">
                        <div class="flex">
                            <p>Filters</p>
                            <span class="ml-4 text-13-120 lg:ml-8">{filters.length}</span>
                        </div>
                        <ToggleSidebarButton />
                    </div>

                    <div class="ns-sidebar-menu flex flex-col lg:flex-row lg:justify-between lg:px-24 placeholder-h-facet-bar text-13-120--0.5-700">
                        <ul class="ns-list-unstyled ns-p-0 ns-m-0 lg:flex lg:gap-24">
                            {facets?.map(facet => {
                                switch (facet.type) {
                                    case "terms":
                                        return <Facet 
                                        key={facet.id} 
                                        facet={facet} 
                                        isActive={activeFacet === facet.id} 
                                        setActive={(active) => {
                                            if (active) {
                                                setActiveFacet(facet.id)
                                            } else {
                                                setActiveFacet('')
                                            }
                                        }}
                                        selectedItems={selectedItems}
                                        setSelectedItems={setSelectedItems}
                                        />
                                    case "stats":
                                        return <RangeFacet 
                                        key={facet.id} 
                                        facet={facet} 
                                        isActive={activeFacet === facet.id} 
                                        setActive={(active) => {
                                            if (active) {
                                                setActiveFacet(facet.id)
                                            } else {
                                                setActiveFacet('')
                                            }
                                        }}
                                        selectedItems={selectedItems}
                                        setSelectedItems={setSelectedItems}
                                        />
                                    default:
                                        return null
                                }
                            })}
                        </ul>

                        <div class="ns-relative lg:flex lg:gap lg:gap-24 lg:items-center text-13-120--0.5-700 capitalize">
                            <div class="hidden lg:flex gap-12 items-center">
                                <span>Grid</span>
                                <div className="flex gap-12">
                                    <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 12 13"
                                    height="12"
                                    width="16"
                                    class={`cursor-pointer ${GRID_CLASSES.SMALL}`}
                                    style={{ strokeOpacity: gridClass === GRID_CLASSES.SMALL ? 1 : 0.3 }}
                                    onClick={() => handleGridChange(GRID_CLASSES.SMALL)}
                                    >
                                        <path stroke="#000" d="M.5 1h11v11H.5z" />
                                    </svg>

                                    <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 12 13"
                                    height="12"
                                    width="22"
                                    class={`cursor-pointer ${GRID_CLASSES.LARGE}`}
                                    style={{ strokeOpacity: gridClass === GRID_CLASSES.LARGE ? 1 : 0.3 }}
                                    onClick={() => handleGridChange(GRID_CLASSES.LARGE)}
                                    >
                                        <path stroke="#000" d="M.5 1h4v11h-4zM7.5 1h4v11h-4z" />
                                    </svg>

                                </div>
                            </div>

                            <FilterSelectGrebban
                                value={activeSort}
                                onChange={e => handleSortChange(e)}
                                className=""
                                options={options}
                                label={"Sort by"} 
                            />
                        </div>
                    </div>

                    <div class="fixed bottom-0 w-full flex gap-24 shrink-0 flex items-end px-12 py-24  border-t border-black-15 ns-background-white z-filter-drawer-layer-mobile lg:hidden">
                        <label for={toggleButtonId} class="btn-fill btn-fill-primary-black whitespace-nowrap">
                            <span>{addFilterLabel}</span>
                        </label>
                        {hasActiveFilters && (
                        <button
                            onClick={removeAll}
                            class="btn-fill btn-fill-primary-black whitespace-nowrap"
                        >
                            <span>{clearLabel}</span>
                        </button>
                        )}
                    </div>
                </div>
            </div>
            <div class={`hidden lg:absolute inset-0 bg-black-20 backdrop-blur-[20px] cursor-close z-40 ${activeFacet? 'lg:block' : 'lg:hidden'}`} onClick={()=> setActiveFacet('')}></div>
        </>
    ) : (
        ""
    )
}
